import {
  ActivatedRoute,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { tap } from 'rxjs';
import { Store } from '@ngrx/store';
import {
  selectCoreLanguage,
  selectMainMenuCanToggle,
  selectMainMenuItems,
  selectMainMenuSecondaryItems,
} from '../../../core/state';
import { MainMenuStateService } from '../../services/main-menu-state.service';

import { AppState } from 'src/app/core/state/app.state';
import { MenuItem } from 'src/app/core/models/main-menu.models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { ModuleSwitchComponent } from '../module-switch/module-switch.component';
import { SearchInputComponent } from '../../../shared/components/search-input/search-input.component';
import { SuiTooltipDirective } from '@sui/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { HighlightPipe } from '../../../shared/pipes/highlight.pipe';

@Component({
  selector: 'app-main-menu',
  templateUrl: './main-menu.component.html',
  styleUrls: ['./main-menu.component.scss'],
  imports: [
    FaIconComponent,
    ModuleSwitchComponent,
    SearchInputComponent,
    RouterLink,
    RouterLinkActive,
    SuiTooltipDirective,
    TranslatePipe,
    HighlightPipe,
  ],
})
export class MainMenuComponent implements OnInit {
  @ViewChild('searchInput') searchInput: ElementRef;

  private readonly menuState = inject(MainMenuStateService);

  readonly isExpanded = this.menuState.isExpanded;
  readonly menuItems = this.store.selectSignal(selectMainMenuItems);
  readonly menuItemsSecondary = this.store.selectSignal(
    selectMainMenuSecondaryItems,
  );
  readonly mainMenuCanToggle = this.store.selectSignal(selectMainMenuCanToggle);
  private readonly language = this.store.selectSignal(selectCoreLanguage);
  readonly isRtl = computed(() => this.language()?.id === 6);

  hasUser: boolean = true;
  readonly searchString = signal('');
  filtered: boolean = false;
  readonly searchEnabled = signal(false);
  hasBackButton: boolean = false;

  @Output() onBackClick: EventEmitter<any> = new EventEmitter();

  private destroyRef = inject(DestroyRef);

  constructor(
    private store: Store<AppState>,
    private router: Router,
    public route: ActivatedRoute,
  ) {}

  ngOnInit() {
    this.router.events
      .pipe(
        tap(() => {
          this.markActiveMenuItem();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  isGroupCollapsed(menuItem: MenuItem): boolean {
    return this.menuState.isGroupCollapsed(menuItem.key);
  }

  toggleMenuHeader(menuItem: MenuItem) {
    if (menuItem.isHeader && menuItem.key) {
      this.menuState.toggleGroup(menuItem.key);
    }
  }

  toggleSidebar() {
    this.menuState.togglePinnedMode();

    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);
  }

  back() {
    this.onBackClick.emit(true);
  }

  toggleSearch() {
    this.searchEnabled.update((enabled) => !enabled);
    if (!this.searchEnabled()) {
      this.searchString.set('');
    }
  }

  onSearchChange(value: any) {
    if (typeof value === 'string') {
      this.searchString.set(value);
    }
  }

  itemClick(item: MenuItem) {
    if (item.link) {
      this.router.navigate([item.link]);
    }
  }

  markActiveMenuItem() {}

  searchChange() {}
}
